import { useEffect, useState } from "react";
import { FileUp, Loader2, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSEO } from "../hooks/useSEO";

const STATUS_STYLES = {
  pending_payment: "border-yellow-500/30 text-yellow-400",
  paid: "border-[#17B890]/30 text-[#17B890]",
  delivered: "border-[#D4AF37]/40 text-[#D4AF37]",
};

const emptyPost = { title: "", excerpt: "", content: "", meta_description: "", published: false };

export default function AdminDashboard() {
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState(null);
  const [posts, setPosts] = useState(null);
  const [reportFor, setReportFor] = useState(null);
  const [reportText, setReportText] = useState("");
  const [savingReport, setSavingReport] = useState(false);
  const [postForm, setPostForm] = useState(null);
  const [savingPost, setSavingPost] = useState(false);
  useSEO("Admin Dashboard | NAKSRA", "NAKSRA admin dashboard.");

  const loadOrders = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadPosts = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    loadOrders();
    loadPosts();
  }, []);

  const submitReport = async () => {
    setSavingReport(true);
    try {
      await api.post(`/admin/orders/${reportFor.id}/report`, { report_text: reportText });
      toast.success("Report published — customer has been emailed.");
      setReportFor(null);
      setReportText("");
      loadOrders();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingReport(false);
    }
  };

  const submitPost = async () => {
    setSavingPost(true);
    try {
      if (postForm.id) await api.put(`/admin/blog/${postForm.id}`, postForm);
      else await api.post("/admin/blog", postForm);
      toast.success(postForm.published ? "Article published." : "Draft saved.");
      setPostForm(null);
      loadPosts();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingPost(false);
    }
  };

  const deletePost = async (p) => {
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success("Article deleted.");
      loadPosts();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const inputCls =
    "w-full rounded-lg border border-[#27272A] bg-[#050507] px-4 py-3 text-sm text-white placeholder-[#52525B] outline-none transition-colors duration-300 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40";

  return (
    <div className="min-h-screen bg-[#050507] text-[#F8F9FA]">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-16" data-testid="admin-dashboard">
        <p className="text-xs font-medium uppercase tracking-[0.35em] text-[#D4AF37]">NAKSRA Admin</p>
        <h1 className="mt-4 font-heading text-4xl font-medium tracking-tight">Dashboard</h1>

        <div className="mt-10 flex gap-2 border-b border-white/10">
          <button
            onClick={() => setTab("orders")}
            data-testid="admin-orders-tab"
            className={`px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${tab === "orders" ? "border-b-2 border-[#D4AF37] text-[#D4AF37]" : "text-[#A1A1AA] hover:text-white"}`}
          >
            Orders
          </button>
          <button
            onClick={() => setTab("blog")}
            data-testid="admin-blog-tab"
            className={`px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${tab === "blog" ? "border-b-2 border-[#D4AF37] text-[#D4AF37]" : "text-[#A1A1AA] hover:text-white"}`}
          >
            Sangha
          </button>
        </div>

        {tab === "orders" && (
          <div className="mt-8" data-testid="admin-orders-list">
            {orders === null ? (
              <p className="text-[#A1A1AA]">Loading orders…</p>
            ) : orders.length === 0 ? (
              <div className="rounded-lg border border-white/10 bg-[#0C0C10] p-12 text-center text-[#A1A1AA]" data-testid="admin-orders-empty">
                No orders yet.
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className="rounded-lg border border-white/10 bg-[#0C0C10] p-6" data-testid={`admin-order-${o.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3">
                          <h2 className="font-heading text-2xl font-medium">{o.service_name}</h2>
                          <span className={`rounded-full border px-3 py-0.5 text-xs ${STATUS_STYLES[o.status] || "border-white/20 text-[#A1A1AA]"}`} data-testid={`admin-order-status-${o.id}`}>
                            {o.status.replace(/_/g, " ")}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-[#A1A1AA]">
                          {o.user_name} ({o.user_email}) · ${o.price.toFixed(2)} · {new Date(o.created_at).toLocaleString()}
                        </p>
                      </div>
                      {o.status === "paid" && (
                        <button
                          onClick={() => { setReportFor(o); setReportText(""); }}
                          data-testid={`upload-report-btn-${o.id}`}
                          className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-2.5 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D] active:scale-[0.98]"
                        >
                          <FileUp className="h-4 w-4" /> Upload report
                        </button>
                      )}
                      {o.status === "delivered" && (
                        <button
                          onClick={() => { setReportFor(o); setReportText(""); }}
                          data-testid={`update-report-btn-${o.id}`}
                          className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-2.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/5"
                        >
                          <FileUp className="h-4 w-4" /> Replace report
                        </button>
                      )}
                    </div>
                    <div className="mt-4 grid gap-3 border-t border-white/10 pt-4 text-sm text-[#A1A1AA] sm:grid-cols-2 lg:grid-cols-4">
                      <span>Chart for: <span className="text-white">{o.full_name}</span></span>
                      <span>Born: <span className="text-white">{o.birth_date} {o.birth_time}</span></span>
                      <span>Place: <span className="text-white">{o.birth_place}</span></span>
                      {o.focus_area && <span>Focus: <span className="text-white">{o.focus_area}</span></span>}
                      {o.partner_name && (
                        <span className="sm:col-span-2">
                          Partner: <span className="text-white">{o.partner_name} · {o.partner_birth_date} {o.partner_birth_time || ""} · {o.partner_birth_place || ""}</span>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "blog" && (
          <div className="mt-8" data-testid="admin-blog-list">
            <button
              onClick={() => setPostForm({ ...emptyPost })}
              data-testid="new-post-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-2.5 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D] active:scale-[0.98]"
            >
              <Plus className="h-4 w-4" /> New article
            </button>
            <div className="mt-6 space-y-4">
              {posts === null ? (
                <p className="text-[#A1A1AA]">Loading articles…</p>
              ) : posts.length === 0 ? (
                <div className="rounded-lg border border-white/10 bg-[#0C0C10] p-12 text-center text-[#A1A1AA]">No articles yet.</div>
              ) : (
                posts.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-4 rounded-lg border border-white/10 bg-[#0C0C10] p-5" data-testid={`admin-post-${p.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <h2 className="truncate font-heading text-xl font-medium">{p.title}</h2>
                        <span className={`shrink-0 rounded-full border px-3 py-0.5 text-xs ${p.published ? "border-[#17B890]/30 text-[#17B890]" : "border-white/20 text-[#A1A1AA]"}`}>
                          {p.published ? "Published" : "Draft"}
                        </span>
                      </div>
                      <p className="mt-1 truncate text-xs text-[#A1A1AA]">/blog/{p.slug}</p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button onClick={() => setPostForm({ ...p })} data-testid={`edit-post-btn-${p.id}`} className="rounded-full border border-white/20 p-2.5 text-white transition-colors duration-300 hover:bg-white/5">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => deletePost(p)} data-testid={`delete-post-btn-${p.id}`} className="rounded-full border border-red-500/30 p-2.5 text-red-400 transition-colors duration-300 hover:bg-red-500/10">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* Report upload modal */}
      {reportFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6 backdrop-blur-sm" data-testid="report-modal">
          <div className="w-full max-w-2xl rounded-lg border border-white/10 bg-[#0C0C10] p-8">
            <h2 className="font-heading text-2xl font-medium">Upload report — {reportFor.service_name}</h2>
            <p className="mt-1 text-sm text-[#A1A1AA]">For {reportFor.full_name} · Order {reportFor.id.slice(0, 8).toUpperCase()}. The customer is emailed automatically on publish.</p>
            <textarea
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              data-testid="report-text-input"
              rows={12}
              className={`${inputCls} mt-6 font-mono`}
              placeholder="Paste the completed, astrologer-verified report text here…"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setReportFor(null)} data-testid="report-cancel-btn" className="rounded-full border border-white/20 px-6 py-2.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/5">
                Cancel
              </button>
              <button onClick={submitReport} disabled={savingReport} data-testid="report-submit-btn" className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-2.5 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D] disabled:opacity-60">
                {savingReport && <Loader2 className="h-4 w-4 animate-spin" />} Publish report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Blog editor modal */}
      {postForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/70 p-6 backdrop-blur-sm" data-testid="post-modal">
          <div className="w-full max-w-2xl rounded-lg border border-white/10 bg-[#0C0C10] p-8">
            <h2 className="font-heading text-2xl font-medium">{postForm.id ? "Edit article" : "New article"}</h2>
            <div className="mt-6 space-y-5">
              <div>
                <label htmlFor="post-title" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Title</label>
                <input id="post-title" data-testid="post-title-input" value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} className={inputCls} placeholder="Article title (used in the URL)" />
              </div>
              <div>
                <label htmlFor="post-excerpt" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Excerpt</label>
                <input id="post-excerpt" data-testid="post-excerpt-input" value={postForm.excerpt} onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })} className={inputCls} placeholder="One-sentence summary shown in listings" />
              </div>
              <div>
                <label htmlFor="post-meta" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Meta description (SEO, max 160 chars)</label>
                <input id="post-meta" data-testid="post-meta-input" maxLength={160} value={postForm.meta_description} onChange={(e) => setPostForm({ ...postForm, meta_description: e.target.value })} className={inputCls} placeholder="Shown in Google search results" />
              </div>
              <div>
                <label htmlFor="post-content" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Content</label>
                <textarea id="post-content" data-testid="post-content-input" rows={10} value={postForm.content} onChange={(e) => setPostForm({ ...postForm, content: e.target.value })} className={inputCls} placeholder="Article body. Blank lines separate paragraphs." />
              </div>
              <label className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                <input type="checkbox" data-testid="post-published-checkbox" checked={postForm.published} onChange={(e) => setPostForm({ ...postForm, published: e.target.checked })} className="h-4 w-4 accent-[#D4AF37]" />
                Publish immediately
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setPostForm(null)} data-testid="post-cancel-btn" className="rounded-full border border-white/20 px-6 py-2.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/5">
                Cancel
              </button>
              <button onClick={submitPost} disabled={savingPost || !postForm.title || !postForm.content} data-testid="post-submit-btn" className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-2.5 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D] disabled:opacity-60">
                {savingPost && <Loader2 className="h-4 w-4 animate-spin" />} {postForm.published ? "Publish" : "Save draft"}
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
